import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";

// Profils vidéo : liste + profil actif + application avec états UX (redémarrage/reconnexion).
export function useVideoProfile(droneId, { onReconnect } = {}) {
  const [profiles, setProfiles] = useState(null); // { default, profiles: {...} }
  const [current, setCurrent] = useState(null);   // { profile, width, height, fps, bitrate }
  const [phase, setPhase] = useState("idle");      // idle|changing|restarting|reconnecting|reconnected|error
  const [error, setError] = useState(null);
  const [blocked, setBlocked] = useState(null);
  const [available, setAvailable] = useState(true);

  const load = useCallback(async () => {
    if (!droneId) return;
    try {
      const [pRes, cRes] = await Promise.allSettled([
        api.get(`/drones/${droneId}/video/profiles`),
        api.get(`/drones/${droneId}/video/profile`),
      ]);
      if (pRes.status === "fulfilled") { setProfiles(pRes.value.data); setAvailable(true); }
      else setAvailable(false);
      if (cRes.status === "fulfilled") setCurrent(cRes.value.data);
    } catch { setAvailable(false); }
  }, [droneId]);

  const apply = useCallback(async (id) => {
    setError(null); setBlocked(null); setPhase("changing");
    try {
      const { data } = await api.post(`/drones/${droneId}/video/profile`, { profile: id });
      if (data.blocked) { setBlocked(data); setPhase("idle"); return; }
      if (!data.ok) { setError(data.error || "Échec"); setPhase("error"); return; }
      setPhase("restarting");
      await new Promise((r) => setTimeout(r, 3500)); // redémarrage MediaMTX / pipeline
      await load();
      setPhase("reconnecting");
      onReconnect?.();
      setTimeout(() => setPhase("reconnected"), 4000);
      setTimeout(() => setPhase("idle"), 8000);
    } catch (e) {
      setError(e?.response?.data?.detail || "Échec du changement de profil");
      setPhase("error");
    }
  }, [droneId, load, onReconnect]);

  useEffect(() => { load(); }, [load]);

  return { profiles, current, phase, error, blocked, available, apply, reload: load };
}

export const PROFILE_PHASE_LABEL = {
  changing: "Changement de profil…",
  restarting: "Redémarrage caméra…",
  reconnecting: "Reconnexion WebRTC…",
  reconnected: "Flux reconnecté",
  error: "Échec",
  idle: null,
};
